import { forwardRef, type ReactNode } from 'react';

import {
  Switch as RACSwitchField,
  type SwitchProps as RACSwitchFieldProps,
} from 'react-aria-components/Switch';

import { cx } from '../../utils/cx';
import { FieldDescription } from '../form/field_description';
import { FieldLabel } from '../form/field_label';

import styles from './switch_field.module.css';

export type SwitchProps = RACSwitchFieldProps & {
  className?: string;
  description?: string;
  isDisabled?: boolean;
  label?: string;
  labelPosition?: 'start' | 'end';
  tooltip?: ReactNode;
  value: string;
  fullWidth?: boolean;
};

/**
 * Switch component that can be used for form fields.
 *
 * <SwitchField label="Label" description="Description" />
 * <SwitchField aria-label="Label" />
 * <SwitchField label="Label" labelPosition="left" />
 *
 * See also https://react-spectrum.adobe.com/react-aria/Switch.html
 */

export const SwitchField = forwardRef<HTMLLabelElement, SwitchProps>(
  (
    {
      className,
      description,
      isReadOnly,
      label,
      labelPosition = 'end',
      tooltip,
      fullWidth,
      ...props
    },
    ref,
  ) => {
    const labelNode = label ? (
      <FieldLabel className={styles.switch_field_label} tooltip={tooltip}>
        {label}
      </FieldLabel>
    ) : null;

    return (
      <RACSwitchField
        ref={ref}
        className={cx([styles.switch_field, className])}
        isReadOnly={isReadOnly}
        {...props}
      >
        <div
          className={cx([
            styles.switch_wrapper,
            {
              [styles.label_start]: labelPosition === 'start',
              [styles.switch_wrapper_full_width]: fullWidth,
            },
          ])}
        >
          {labelNode}
          <span className={styles.indicator} />
        </div>
        {description && <FieldDescription>{description}</FieldDescription>}
      </RACSwitchField>
    );
  },
);
